import {
  CommendaAiReferenceType,
  type CommendaAiReferencePreview,
  type CommendaAiReferenceRow,
} from '@commenda/api/commenda-ai/types';
import { useChatReferencePreview } from '@commenda/api/commenda-ai/useChatReferencePreview';
import type { CountryCode } from '@commenda/kakashi';
import { Avatar, Badge, FlagComponent } from '@commenda/kakashi';
import { ServiceRequestStatusBadgeHelper } from '@commenda/utils';
import { Country as CountryPkg } from 'country-state-city';
import { useState, type ReactElement } from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../ui/hover-card';
import type { ChatReference } from './ChatReferenceHelper';

function PreviewRow({ row }: { row: CommendaAiReferenceRow }) {
  return (
    <div className="flex items-start gap-md">
      <span className="w-[88px] shrink-0 txt-extra-small-regular text-text-tertiary">
        {row.label}
      </span>
      <span className="flex min-w-0 flex-1 items-start gap-md">
        <span className="min-w-0 flex-1 break-words txt-extra-small-medium text-text-primary">
          {row.value}
        </span>
        {row.overflow && (
          <span className="shrink-0 txt-extra-small-regular text-text-tertiary">
            {row.overflow}
          </span>
        )}
      </span>
    </div>
  );
}

function PreviewHeader({
  preview,
  referenceId,
}: {
  preview: CommendaAiReferencePreview;
  referenceId: string;
}) {
  const badge = preview.status
    ? ServiceRequestStatusBadgeHelper.getBadgeProps({
        status: preview.status,
        isOnHold: Boolean(preview.isOnHold),
      })
    : undefined;

  return (
    <div className="flex items-center gap-md">
      {preview.country && (
        <FlagComponent
          country={preview.country as CountryCode}
          countryName={
            CountryPkg.getCountryByCode(preview.country)?.name ??
            preview.country
          }
          _className="size-6"
        />
      )}

      {preview.type === CommendaAiReferenceType.KEY_PERSON && (
        <Avatar
          size="xs"
          name={preview.title}
          initialsCount={2}
          colorSeed={referenceId}
        />
      )}

      <span className="min-w-0 flex-1 break-words txt-small-medium text-text-primary">
        {preview.title}
      </span>

      {badge?.text && (
        <Badge size="sm" variant={badge.variant}>
          {badge.text}
        </Badge>
      )}
    </div>
  );
}

function PreviewSkeleton() {
  return (
    <div className="flex flex-col gap-lg">
      <div className="h-5 w-2/3 animate-pulse rounded-xs bg-bg-secondary" />
      <div className="h-px w-full bg-border-secondary" />
      <div className="flex flex-col gap-sm">
        <div className="h-4 w-full animate-pulse rounded-xs bg-bg-secondary" />
        <div className="h-4 w-4/5 animate-pulse rounded-xs bg-bg-secondary" />
        <div className="h-4 w-3/5 animate-pulse rounded-xs bg-bg-secondary" />
      </div>
    </div>
  );
}

export function ChatReferencePopover({
  reference,
  children,
}: {
  reference: ChatReference;
  children: ReactElement;
}) {
  const [isOpen, setOpen] = useState(false);
  const { preview, isLoading, error } = useChatReferencePreview(
    reference.type,
    reference.referenceId,
    isOpen
  );

  return (
    <HoverCard
      openDelay={100}
      closeDelay={150}
      open={isOpen}
      onOpenChange={setOpen}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        className="flex w-80 flex-col gap-lg rounded-md border-[0.5px] border-border-primary bg-bg-primary p-lg shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)]"
      >
        {isLoading && <PreviewSkeleton />}

        {error && (
          <span className="txt-extra-small-regular text-text-tertiary">
            Preview unavailable
          </span>
        )}

        {preview && (
          <>
            <PreviewHeader
              preview={preview}
              referenceId={reference.referenceId}
            />
            {preview.rows.length > 0 && (
              <>
                <div className="h-px w-full bg-border-secondary" />
                <div className="flex flex-col gap-sm">
                  {preview.rows.map((row) => (
                    <PreviewRow key={`${row.label}-${row.value}`} row={row} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
